import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { AlertCircle, CheckCircle2, Loader2, RefreshCw, Zap } from "lucide-react";
import { api, SyncHealth } from "@/lib/api";

interface BulkSyncLog {
  index?: number;
  total?: number;
  pct?: number;
  sku?: string;
  title?: string;
  status?: string;
  operation?: string;
  reason?: string;
  error?: string;
  type: string;
  succeeded?: number;
  failed?: number;
}

export function SyncHealthStrip() {
  const qc = useQueryClient();
  const { data: health, isLoading } = useQuery<SyncHealth>({
    queryKey: ["sync-health"],
    queryFn: api.syncHealth,
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  const [running, setRunning] = useState(false);
  const [runTotal, setRunTotal] = useState<number | null>(null);
  const [log, setLog] = useState<BulkSyncLog[]>([]);
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState<{ succeeded: number; failed: number; total: number } | null>(null);
  const esRef = useRef<EventSource | null>(null);
  const logRef = useRef<HTMLDivElement | null>(null);

  const startBulkSync = useCallback(async () => {
    if (running) return;
    setRunning(true);
    setRunTotal(null);
    setLog([]);
    setProgress(0);
    setSummary(null);

    try {
      const run = await api.bulkSync();
      // Use the total from the HTTP response immediately — don't wait for the
      // "started" SSE event which may arrive before EventSource connects.
      setRunTotal(run.total ?? 0);
      setLog([{ type: "started", title: `Starting sync of ${run.total ?? 0} products…` }]);

      if ((run.total ?? 0) === 0) {
        // Nothing to sync — SSE will confirm with "done", but show result now.
        setProgress(100);
        setSummary({ succeeded: 0, failed: 0, total: 0 });
        setRunning(false);
        qc.invalidateQueries({ queryKey: ["sync-health"] });
        return;
      }

      const es = new EventSource(`/api/store/sync/bulk/stream?run_id=${run.run_id}`);
      esRef.current = es;

      es.onmessage = (e) => {
        const msg = JSON.parse(e.data) as BulkSyncLog;
        if (msg.type === "progress") {
          setProgress(msg.pct ?? 0);
          setLog((l) => [...l, msg]);
          setTimeout(() => {
            if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
          }, 50);
        } else if (msg.type === "done") {
          setProgress(100);
          setSummary({ succeeded: msg.succeeded ?? 0, failed: msg.failed ?? 0, total: msg.total ?? 0 });
          setRunning(false);
          es.close();
          esRef.current = null;
          qc.invalidateQueries({ queryKey: ["sync-health"] });
          qc.invalidateQueries({ queryKey: ["store-products"] });
        } else if (msg.type === "started") {
          setRunTotal(msg.total ?? null);
        }
      };
      es.onerror = () => {
        setRunning(false);
        es.close();
        esRef.current = null;
        // Refresh product list even if we missed the "done" event (30s timeout race).
        qc.invalidateQueries({ queryKey: ["store-products"] });
        qc.invalidateQueries({ queryKey: ["sync-health"] });
      };
    } catch {
      setRunning(false);
    }
  }, [running, qc]);

  // Cleanup on unmount
  useEffect(() => () => { esRef.current?.close(); }, []);

  const hasIssues = health && (health.drifted_price > 0 || health.drifted_status > 0 || health.missing_shopify > 0);
  const allGood = health && !hasIssues && health.total_published > 0;

  return (
    <div className="space-y-3">
      {/* Health strip */}
      <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg border border-border bg-card">
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : health ? (
          <>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <span className="text-sm font-mono tabular-nums text-emerald-600 font-medium">
                {health.total_published}
              </span>
              <span className="text-xs text-muted-foreground">published</span>
            </div>

            {health.drifted_price > 0 && (
              <div className="flex items-center gap-1.5">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                <span className="text-sm font-mono tabular-nums text-amber-600 font-medium">
                  {health.drifted_price}
                </span>
                <span className="text-xs text-muted-foreground">price drift</span>
              </div>
            )}

            {health.drifted_status > 0 && (
              <div className="flex items-center gap-1.5">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                <span className="text-sm font-mono tabular-nums text-amber-600 font-medium">
                  {health.drifted_status}
                </span>
                <span className="text-xs text-muted-foreground">status drift</span>
              </div>
            )}

            {health.missing_shopify > 0 && (
              <div className="flex items-center gap-1.5">
                <AlertCircle className="h-4 w-4 text-rose-500" />
                <span className="text-sm font-mono tabular-nums text-rose-600 font-medium">
                  {health.missing_shopify}
                </span>
                <span className="text-xs text-muted-foreground">missing in Shopify</span>
              </div>
            )}

            {allGood && (
              <span className="text-xs text-emerald-600">All products in sync</span>
            )}

            {health.last_bulk_sync_at && (
              <span className="text-xs text-muted-foreground font-mono ml-auto">
                Last sync: {new Date(health.last_bulk_sync_at).toLocaleString()}
              </span>
            )}
          </>
        ) : (
          <span className="text-xs text-muted-foreground">Sync health unavailable</span>
        )}

        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => qc.invalidateQueries({ queryKey: ["sync-health"] })}
            title="Refresh health"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <Button
            onClick={startBulkSync}
            disabled={running}
            variant="ember"
            size="sm"
            className="gap-1.5"
          >
            {running
              ? <><Loader2 className="h-4 w-4 animate-spin" />Syncing…</>
              : <><Zap className="h-4 w-4" />Bulk Sync</>}
          </Button>
        </div>
      </div>

      {/* Progress panel — shows when running or just finished */}
      {(running || log.length > 0) && (
        <div className="rounded-lg border border-border bg-card p-4 space-y-3">
          {running && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {log.filter((l) => l.type === "progress").length} / {runTotal ?? "?"} products
                </span>
                <span className="font-mono">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          {summary && (
            <div className="flex items-center gap-3 text-sm">
              <span className="text-emerald-600 font-medium">
                ✓ {summary.succeeded} synced
              </span>
              {summary.failed > 0 && (
                <span className="text-rose-600 font-medium">
                  ✗ {summary.failed} failed
                </span>
              )}
              <span className="text-muted-foreground text-xs">
                {summary.succeeded} / {summary.total} products processed
              </span>
            </div>
          )}

          <div
            ref={logRef}
            className="h-40 overflow-y-auto rounded-md border border-border bg-muted/30 p-2.5 text-xs font-mono space-y-0.5"
          >
            {log.map((entry, i) => {
              const isError = entry.status === "error";
              const isSkip = entry.status === "skipped" || entry.status === "missing";
              const color = isError
                ? "text-rose-500"
                : isSkip
                ? "text-amber-500"
                : "text-emerald-600";
              return (
                <div key={i} className="flex gap-2">
                  <span className="opacity-40 shrink-0">
                    {entry.index ? String(entry.index).padStart(3, "0") : "···"}
                  </span>
                  <span className={`${color} truncate`}>
                    {entry.type === "started"
                      ? entry.title
                      : entry.sku
                      ? `${entry.sku} — ${entry.status}${entry.operation ? ` (${entry.operation})` : ""}${entry.reason ? `: ${entry.reason}` : ""}${entry.error ? `: ${entry.error}` : ""}`
                      : JSON.stringify(entry)}
                  </span>
                </div>
              );
            })}
            {running && (
              <div className="flex gap-2 text-muted-foreground animate-pulse">
                <span className="opacity-40">···</span>
                <span>Processing…</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Needs attention list */}
      {health && health.needs_attention.length > 0 && !running && (
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 space-y-1.5">
          <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">
            {health.needs_attention_count} products not synced in 25+ hours
          </p>
          <div className="grid grid-cols-2 gap-1">
            {health.needs_attention.slice(0, 6).map((item) => (
              <div key={item.id} className="text-xs font-mono text-muted-foreground truncate">
                {item.sku}
              </div>
            ))}
            {health.needs_attention.length > 6 && (
              <div className="text-xs text-muted-foreground col-span-2">
                +{health.needs_attention.length - 6} more
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
